'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/mobile-app/finding-your-way.mdx
 * (published at user-docs.totalaccess.co.za/mobile-app/finding-your-way)
 */

import React, { useEffect, useState } from 'react';
import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import { wsClient } from '@/lib/websocket';
import type { ConnectionStatus } from '@/lib/websocket';
import { networkState } from '@/lib/networkState';
import type { LucideIcon } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type RealtimeStatus = 'live' | 'online' | 'offline';

export interface CapacitorModalHeaderProps {
  /** Lucide icon component for the pill on the left */
  icon?: LucideIcon;
  /** Pre-rendered icon element (takes priority over `icon` prop) */
  customIcon?: React.ReactNode;
  /** Title text, centered */
  title: string;
  /** Extra elements rendered inline after the title (e.g. training badges, timer) */
  titleExtra?: React.ReactNode;
  /** Custom subtitle content (counts, date, stats, etc.) rendered before the WS indicator */
  subtitle?: React.ReactNode;
  /** Controls whether the title centers within the content area or the full header row */
  titleAlignment?: 'content' | 'viewport';
  /** Primary/accent color */
  accentColor: string;
  /** Action buttons rendered on the right side */
  actions?: React.ReactNode;
  /**
   * Optional slim row rendered below the accent bar (e.g. embedded desktop call toggles).
   */
  secondaryToolbar?: React.ReactNode;
  /** Compact actions immediately after the icon (e.g. refresh), before the title */
  leadingActions?: React.ReactNode;
  /** Visual variant: 'default' uses simple bg, 'smarttask' uses frosted-glass surface */
  variant?: 'default' | 'smarttask';
  /** Whether header is on a mobile Capacitor app (controls safe-area padding) */
  isMobile?: boolean;
  /** Apply top safe-area padding even when not driven by shared header-visibility state */
  applyTopSafeArea?: boolean;
  /** Force hide safe-area padding even on mobile (when parent Header is visible above) */
  headerHidden?: boolean;
  /** Override icon style with SmartTask-style bordered pill */
  borderedIcon?: boolean;
  /** Additional className for the header outer container */
  className?: string;
  /** Use gradient accent bar (SmartTask style) instead of solid */
  gradientAccent?: boolean;
  /** Dark mode override (auto-detected via class if not provided) */
  isDark?: boolean;
  /** Show the realtime connection dot + label (Live / Online / Offline). Default true. */
  showRealtimeStatus?: boolean;
  /** data-tour-id attribute for guided tours */
  tourId?: string;
}

// ============================================================================
// useRealtimeStatus hook — shared WS + network status
// ============================================================================

/**
 * Derives a unified realtime status from TotalAccessWebSocket connection
 * and browser network state.
 *
 * - 'live'    = WebSocket connected + authenticated
 * - 'online'  = Network available but WS not connected
 * - 'offline' = No network
 */
export function useRealtimeStatus(): RealtimeStatus {
  const [wsStatus, setWsStatus] = useState<ConnectionStatus>(() => wsClient.getStatus());
  const [isOnline, setIsOnline] = useState(() => networkState.isOnline());

  useEffect(() => {
    const unsubWs = wsClient.onConnection((status) => {
      setWsStatus(status);
    });

    const unsubNet = networkState.subscribe((state) => {
      setIsOnline(state.isOnline);
    });

    return () => {
      unsubWs();
      unsubNet();
    };
  }, []);

  if (wsStatus === 'connected') return 'live';
  if (isOnline) return 'online';
  return 'offline';
}

// ============================================================================
// Helpers
// ============================================================================

/** SmartTask alpha helper (matches smartTaskAlpha from theme.ts) */
function alphaHex(color: string, alpha: number): string {
  return `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
}

const STATUS_DOT_CLASS: Record<RealtimeStatus, string> = {
  live: 'bg-green-500',
  online: 'bg-amber-400',
  offline: 'bg-gray-400',
};

const STATUS_LABEL: Record<RealtimeStatus, string> = {
  live: 'Live',
  online: 'Online',
  offline: 'Offline',
};

// ============================================================================
// Component
// ============================================================================

export default function CapacitorModalHeader({
  icon: Icon,
  customIcon,
  title,
  titleExtra,
  subtitle,
  titleAlignment = 'content',
  accentColor,
  actions,
  secondaryToolbar,
  leadingActions,
  variant = 'default',
  isMobile = false,
  applyTopSafeArea = false,
  headerHidden = false,
  borderedIcon = false,
  className = '',
  gradientAccent = false,
  isDark,
  showRealtimeStatus = true,
  tourId,
}: CapacitorModalHeaderProps) {
  const realtimeStatus = useRealtimeStatus();

  // Surface classes
  const surfaceClass =
    variant === 'smarttask'
      ? 'border-b border-slate-200/70 bg-white/90 shadow-[0_10px_30px_rgba(15,23,42,0.08)] dark:border-slate-700/70 dark:bg-slate-900/88 dark:shadow-[0_18px_40px_rgba(2,6,23,0.45)] supports-[backdrop-filter]:bg-white/80 supports-[backdrop-filter]:backdrop-blur-xl dark:supports-[backdrop-filter]:bg-slate-900/72'
      : isDark !== undefined
        ? `border-b shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`
        : 'border-b shadow-sm bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700';
  const contentSpacingClass = gradientAccent ? 'py-2.5 gap-3' : 'py-1.5 gap-2';
  const leftContent = (
    <>
      {customIcon ? (
        customIcon
      ) : borderedIcon && Icon ? (
        <div
          className="flex-shrink-0 rounded-2xl border p-2"
          style={{
            borderColor: `${accentColor}30`,
            backgroundColor: `${accentColor}10`,
            color: accentColor,
          }}
        >
          <Icon className="w-4 h-4" />
        </div>
      ) : Icon ? (
        <div
          className="p-1 rounded-md flex-shrink-0"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: accentColor }} />
        </div>
      ) : null}

      {leadingActions ? (
        <div className="flex flex-shrink-0 items-center gap-0.5 pr-1">{leadingActions}</div>
      ) : null}
    </>
  );

  const titleContent = (
    <div className="min-w-0">
      <div className="flex items-center justify-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
          {title}
        </span>
        {titleExtra}
      </div>
      {(subtitle || showRealtimeStatus) && (
        <div className="flex items-center justify-center gap-1">
          {subtitle}
          {showRealtimeStatus && (
            <>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT_CLASS[realtimeStatus]}`} />
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {STATUS_LABEL[realtimeStatus]}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Safe-area padding
  const paddingStyle: React.CSSProperties | undefined = isMobile
    ? { paddingTop: headerHidden || applyTopSafeArea ? MOBILE_SAFE_AREA_TOP : '0px' }
    : undefined;

  return (
    <div
      data-tour-id={tourId}
      className={`flex-shrink-0 transition-[padding] duration-200 ${surfaceClass} ${className}`}
      style={paddingStyle}
    >
      {/* Accent bar */}
      {gradientAccent ? (
        <div
          className="h-1"
          style={{
            background: `linear-gradient(90deg, ${alphaHex(accentColor, 0.12)} 0%, ${accentColor} 20%, ${alphaHex(accentColor, 0.8)} 100%)`,
          }}
        />
      ) : (
        <div className="h-0.5" style={{ backgroundColor: accentColor }} />
      )}

      {secondaryToolbar ? (
        <div
          className={
            variant === 'smarttask'
              ? 'max-w-4xl mx-auto flex flex-wrap items-center justify-end gap-2 border-b border-slate-200/70 px-3 pb-1 pt-2 dark:border-slate-700/70'
              : `max-w-4xl mx-auto flex flex-wrap items-center justify-end gap-2 border-b px-3 pb-1 pt-2 ${
                  isDark !== undefined
                    ? isDark
                      ? 'border-gray-700/70'
                      : 'border-gray-200'
                    : 'border-gray-200 dark:border-gray-700/70'
                }`
          }
        >
          {secondaryToolbar}
        </div>
      ) : null}

      {titleAlignment === 'viewport' ? (
        <div className={`max-w-4xl mx-auto px-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center ${contentSpacingClass}`}>
          <div className="flex min-w-0 items-center gap-2 justify-self-start">
            {leftContent}
          </div>

          <div className="min-w-0 px-2 text-center">
            {titleContent}
          </div>

          <div className="flex min-w-0 items-center justify-end gap-0.5 justify-self-end">
            {actions}
          </div>
        </div>
      ) : (
        <div className={`max-w-4xl mx-auto px-3 flex items-center ${contentSpacingClass}`}>
          <div className="flex min-w-0 items-center gap-2">
            {leftContent}
          </div>

          <div className="flex-1 min-w-0">
            {titleContent}
          </div>

          {actions && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
